import { useEffect, useState } from "react";
import { openExternal } from "../utils/openExternal";
import { db } from "../services/firebase";
import { useAuth } from "../context/AuthContext";
import { collection, query, orderBy, onSnapshot, where, doc, getDoc, setDoc } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import {
  LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
  CartesianGrid
} from "recharts";
import {
  TrendingDown, Syringe, Activity, BarChart2,
  Plus, Calculator, AlertCircle, ChevronRight, Target, Zap
} from "lucide-react";
import { formatKgTick, formatPercentTick, formatWeekTick } from "../utils/chartFormat";
import {
  calcTherapyWeek,
  calcWeeklyAverageLossKg,
  calcCycleState,
  resolveInjectionAnchor,
  resolveBaseWeight,
  calcTotalLoss,
  calcTotalLossPct,
  selectWeighedEntries,
  calcPeriodSummary,
  resolveTherapyStart,
  formatWeightChange,
  formatWeightChangePct,
  calcGoalPct,
  weightChangeLabel,
} from "../utils/therapyStats";
import { entryTimestampMs, sortEntriesByDateAsc, formatEntryDateLabel, isFutureEntry, selectEntriesEmptyReason, ENTRIES_EMPTY_MESSAGES } from "../utils/entryDate";
import { selectClinicalBranch, selectClosestDose, interpolateWeeklySeries, selectClinicalEmptyStateReason, formatReferenceDoseLabel, formatComparatorLabel, formatDerivedNote } from "../utils/clinicalReferenceSelect";
import { IS_NATIVE } from "../config/platform";

interface Entry {
  id: string;
  // A D2 döntés óta hiányozhat: a súly nélkül mentett bejegyzésben `null`
  // (régi dokumentumban akár nincs is mező). Súly-idősorhoz mindig
  // selectWeighedEntries — a nyers `[0]`/`[last]` ezt nem veszi észre.
  weight?: number | null;
  dose?: number;
  date?: string;
  createdAt?: { seconds: number };
}

interface Profil {
  gyogyszer?: string;
  kezdoSuly?: string;
  celSuly?: string;
  terapiaKezdete?: string;
  therapyReason?: "cukorbetegseg" | "testsulycsokkenes" | "mindketto" | null;
  injectionIntervalDays?: number | null;
  injectionAnchorDate?: string | null;
}

const PHASE_STEPS = [
  { label: "Injekció", day: 0 },
  { label: "Emelkedés", day: 1 },
  { label: "Peak", day: 2 },
  { label: "Stabil", day: 4 },
  { label: "Lejtés", day: 6 },
];

const PHASES = [
  { id: 1, nameHu: "Csúcshatás", icon: "🚀", dayStart: 0, dayEnd: 1, hunger: "alacsony",
    happening: "GLP-1 szint gyorsan emelkedik, étvágy drámaian csökken.",
    attention: "Egyél lassan és kis adagokban. Kerüld a zsíros ételeket." },
  { id: 2, nameHu: "Magas hatás", icon: "⚡", dayStart: 2, dayEnd: 2, hunger: "alacsony",
    happening: "GLP-1 szint a csúcson. Teltségérzet hamarabb jön.",
    attention: "A fogyás szempontjából legerősebb nap. Figyelj a fehérjére." },
  { id: 3, nameHu: "Stabil hatás", icon: "⛰️", dayStart: 3, dayEnd: 4, hunger: "alacsony",
    happening: "Hormonok még aktívak, mellékhatások enyhülnek.",
    attention: "Fókuszálj a rost- és vízfogyasztásra." },
  { id: 4, nameHu: "Fenntartó fázis", icon: "✈️", dayStart: 5, dayEnd: 5, hunger: "közepes",
    happening: "Stabil hatás, jó energia.",
    attention: "Jó alkalom mozgásra. Fehérjebevitel különösen fontos." },
  { id: 5, nameHu: "Lecsengés", icon: "📉", dayStart: 6, dayEnd: 6, hunger: "magas",
    happening: "GLP-1 szint csökken, étvágy visszatérhet.",
    attention: "Légy tudatos az étkezések méretével." },
  { id: 6, nameHu: "Alacsony fázis", icon: "🍎", dayStart: 7, dayEnd: 7, hunger: "magas",
    happening: "Hatás minimális. Injekció napja.",
    attention: "Ma van az injekció! Egyél könnyű ételt előtte." },
];

type TimeRange = "2w" | "1m" | "3m" | "all";
const RANGE_LABELS: Record<TimeRange, string> = { "2w": "2 hét", "1m": "1 hó", "3m": "3 hó", "all": "Összes" };

export default function Fooldal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [profil, setProfil] = useState<Profil>({});
  const [range, setRange] = useState<TimeRange>("all");
  const [showDoses, setShowDoses] = useState(true);
  const [anchorSuggestionDismissed, setAnchorSuggestionDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "entries"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "asc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      // A Firestore-lekérdezés createdAt (beviteli idő) szerint rendez —
      // ez visszamenőleges rögzítésnél rossz sorrendet ad. Minden idősor
      // a date (mikor történt) szerint rendezve legyen.
      const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as Entry[];
      setEntries(sortEntriesByDateAsc(docs));
    });
    getDoc(doc(db, "profiles", user.uid)).then(snap => {
      if (snap.exists()) setProfil(snap.data() as Profil);
    });
    return () => unsubscribe();
  }, [user]);

  // ── Alapszámítások ──
  // Jövőbeli dátumú bejegyzés (elgépelt évszám/nap) kizárva minden
  // számításból — máskülönben "jelenlegi súly"-ként, a trendvonal végeként
  // stb. jelenne meg, mintha megtörtént volna (2026-08-17 QA-lelet). A
  // Napló-listában továbbra is látszik, csak innen, a számításokból esik ki.
  const computableEntries = entries.filter(e => !isFutureEntry(e));
  const doseEntries = computableEntries.filter(e => e.dose && e.dose > 0);
  const lastDoseEntry = doseEntries[doseEntries.length - 1];
  // Súly-idősor: csak a ténylegesen mért bejegyzések. Egy dózis-only sor
  // (D2) nem viheti el a "jelenlegi súly"-t — az továbbra is az utolsó
  // MÉRT súly, akkor is, ha azóta volt súly nélküli injekció-bejegyzés.
  const weighedEntries = selectWeighedEntries(computableEntries);
  const firstWeight = weighedEntries[0]?.weight;
  const lastWeight = weighedEntries[weighedEntries.length - 1]?.weight;
  const prevWeight = weighedEntries[weighedEntries.length - 2]?.weight;
  // A Főoldal mindig a TELJES terápiára vonatkozó kártya (nincs időszak-fül
  // a felső blokkon) — ezért isFullHistory=true: a profil kezdő súlya a
  // kanonikus bázis, fallback az első bejegyzésre. Ugyanez a resolveBaseWeight
  // hívás fut a Statisztika "Összes" nézetében és a PDF-riportban is —
  // közös forrás (docs/FOGALMAK.md).
  const kezdoSulyProfil = resolveBaseWeight(profil.kezdoSuly, firstWeight, true);
  const totalLoss = calcTotalLoss(kezdoSulyProfil, lastWeight);
  const totalLossPct = calcTotalLossPct(kezdoSulyProfil, totalLoss);
  // Változás az utolsó két bejegyzés között — csak a "Jelenlegi súly" kártya
  // kis nyíl-indikátorához, NEM heti ráta (a naplózási gyakoriságtól függ).
  const lastEntryDelta = lastWeight && prevWeight ? (prevWeight - lastWeight).toFixed(1) : null;

  // Célsúly
  const celSuly = profil.celSuly ? parseFloat(profil.celSuly) : null;
  const totalTarget = kezdoSulyProfil && celSuly ? kezdoSulyProfil - celSuly : null;
  const goalPct = calcGoalPct(totalLoss, totalTarget);
  const remainingKg = lastWeight && celSuly ? Math.max(0, lastWeight - celSuly) : null;

  // Terápia hete
  const terapiaKezdete = resolveTherapyStart(profil.terapiaKezdete, computableEntries);
  const therapyWeek = terapiaKezdete ? calcTherapyWeek(terapiaKezdete) : null;

  // Heti átlag — teljes terápiára vetítve (összes fogyás / eltelt hetek),
  // ugyanaz a "therapyWeek", ami a hero-badge-en is látszik. 2026-08-14
  // éles hiba: korábban ez tévesen az utolsó két bejegyzés különbsége volt.
  const weeklyAvgLossKg = calcWeeklyAverageLossKg(totalLoss, therapyWeek);

  // Következő injekció — #86cb5dbkn: állítható ciklushossz + korrigálható
  // horgony-dátum (profiles.injectionIntervalDays/injectionAnchorDate).
  // Horgony hiányában (a legtöbb usernél ma is) a legacyLastInjDate (utolsó
  // dose>0 bejegyzés) a fallback — nincs viselkedésváltozás. Lásd
  // src/utils/therapyStats.ts (resolveInjectionAnchor, calcCycleDays) és
  // docs/FOGALMAK.md.
  const intervalDays = profil.injectionIntervalDays ?? 7;
  const legacyLastInjDate = lastDoseEntry?.date
    ? new Date(lastDoseEntry.date)
    : lastDoseEntry?.createdAt ? new Date(lastDoseEntry.createdAt.seconds * 1000) : null;
  const lastInjDate = resolveInjectionAnchor(profil.injectionAnchorDate, intervalDays, legacyLastInjDate);
  const nextInjDate = lastInjDate ? new Date(lastInjDate.getTime() + intervalDays * 24 * 60 * 60 * 1000) : null;
  // 2026-09-29 (ClickUp 123qmzqympy): a „Lejárt" felirat és a fázis-szöveg
  // EGY forrásból jön. Korábban két külön számítás futott ugyanabból a
  // lastInjDate-ből — a lineáris daysUntil negatívba ment („Lejárt"), a
  // vágott cycleDays viszont intervalDays-en ragadt, ami pont az utolsó
  // fázis („Injekció napja"). A kártya így egyszerre mondta, hogy elmaradt
  // és hogy ma esedékes. A calcCycleState a kanonikus forrás — a daysUntil
  // onnan jön, nem külön képletből (CLAUDE.md 10. szabály).
  const cycleState = lastInjDate ? calcCycleState(lastInjDate, intervalDays) : null;
  const cycleDays = cycleState ? cycleState.cycleDays : null;
  const daysUntil = cycleState ? cycleState.daysUntil : null;
  const isOverdue = cycleState ? cycleState.isOverdue : false;
  const prevDose = doseEntries[doseEntries.length - 2]?.dose;
  const lastDose = lastDoseEntry?.dose;

  // Javaslat-sáv (#86cb5dbkn "felajánlja a frissítést" eleme): ha a 2
  // legutóbbi dózisos bejegyzés a jelenlegi horgony+intervallum modellhez
  // képest KONZISZTENSEN (mindkettő ugyanannyival) eltér, felajánljuk a
  // horgony frissítését — egyetlen kilógó bejegyzés önmagában nem elég.
  const suggestedAnchorDate = (() => {
    if (doseEntries.length < 2 || !lastInjDate) return null;
    const recentTwo = doseEntries.slice(-2);
    const residues = recentTwo.map((e) => {
      const entryMs = entryTimestampMs(e);
      if (!entryMs) return null;
      const diffDays = Math.floor((entryMs - lastInjDate.getTime()) / 86400000);
      return ((diffDays % intervalDays) + intervalDays) % intervalDays;
    });
    if (residues[0] === null || residues[1] === null) return null;
    if (residues[0] === 0 || residues[0] !== residues[1]) return null;
    return recentTwo[1].date || null;
  })();

  // Fázis
  const currentPhaseIdx = cycleDays !== null
    ? PHASE_STEPS.findIndex((s, i) => cycleDays >= s.day && (i === PHASE_STEPS.length - 1 || cycleDays < PHASE_STEPS[i + 1].day))
    : -1;
  const currentPhase = cycleDays !== null
    ? PHASES.find(p => cycleDays >= p.dayStart && cycleDays <= p.dayEnd) || PHASES[4]
    : null;

  // Statisztika — időszűrés. A date (mikor történt) alapján dönt, nem a
  // createdAt (mikor íródott be) alapján — enélkül egy ma visszamenőlegesen
  // felvitt egész éves adatsor mindegyike "2 hét"-ként jelent volna meg.
  const now = Date.now() / 1000;
  const rangeSecs: Record<TimeRange, number> = { "2w": 14 * 86400, "1m": 30 * 86400, "3m": 90 * 86400, "all": Infinity };
  const filtered = computableEntries.filter(e => (now - entryTimestampMs(e) / 1000) <= rangeSecs[range]);

  const filteredWeighed = selectWeighedEntries(filtered);

  const weightData = filteredWeighed.map(e => ({
    date: formatEntryDateLabel(e),
    weight: e.weight,
    dose: e.dose && e.dose > 0 ? e.dose : null,
  }));

  // A "nap 0-tól" a legkorábbi bejegyzés valós date-jétől indul, nem a
  // tömbindextől (2026-08-15 éles hiba).
  // FIGYELEM: ez SZÁNDÉKOSAN NEM a kanonikus kezdoSulyProfil/resolveBaseWeight
  // (fent) — a klinikai trial-görbével való összevetéshez mindig a szűrt
  // (filtered) időszak első bejegyzése a bázis, sosem a profil lifetime
  // kezdő súlya, függetlenül a range-től. Ne vond össze a kettőt egy
  // refaktornál — más kérdésre válaszolnak ("mennyit fogytam ehhez képest,
  // hogy állok a klinikai vizsgálathoz képest" vs. "mennyit fogytam
  // összesen a terápia kezdete óta").
  const baseWeight = filteredWeighed[0]?.weight || 0;
  const trialBaseDateMs = filteredWeighed[0] ? entryTimestampMs(filteredWeighed[0]) : 0;
  const userTrialData = filteredWeighed.map((e) => ({
    week: trialBaseDateMs ? Math.round((entryTimestampMs(e) - trialBaseDateMs) / 86400000 / 7) : 0,
    yourPct: baseWeight && e.weight ? ((e.weight - baseWeight) / baseWeight) * 100 : 0,
  }));

  // Klinikai referencia-ág kiválasztása — docs/KLINIKAI_ADATOK.md, egyetlen
  // forrás: src/data/clinicalReference.ts. Hiányzó/kétértelmű gyógyszer vagy
  // terápia-ok esetén NINCS görbe (üres állapot jobb, mint rossz referencia).
  const clinicalBranch = selectClinicalBranch(profil.gyogyszer, profil.therapyReason);
  const clinicalMatchedDose = clinicalBranch ? selectClosestDose(clinicalBranch, lastDose) : undefined;
  const clinicalReferenceSeries = clinicalBranch && clinicalMatchedDose
    ? interpolateWeeklySeries(clinicalBranch.weeks, clinicalMatchedDose.pctChange)
    : [];
  const clinicalComparatorSeries = clinicalBranch
    ? interpolateWeeklySeries(clinicalBranch.weeks, clinicalBranch.comparatorPct)
    : [];
  const clinicalEmptyReason = !clinicalBranch
    ? selectClinicalEmptyStateReason(profil.gyogyszer, profil.therapyReason)
    : null;

  const doseColors: Record<number, string> = { 2.5: "#9CA3AF", 5: "#7C3AED", 7.5: "#059669", 10: "#2563EB", 12.5: "#0E7490", 15: "#0F172A" };
  const usedDoses = Array.from(new Set(weightData.filter(d => d.dose).map(d => d.dose as number)));

  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (!payload.dose || !showDoses) return null;
    return <circle cx={cx} cy={cy} r={5} fill={doseColors[payload.dose] || "#6B7280"} stroke="white" strokeWidth={2} />;
  };

  const handleAcceptAnchorSuggestion = async () => {
    if (!user || !suggestedAnchorDate) return;
    await setDoc(doc(db, "profiles", user.uid), { injectionAnchorDate: suggestedAnchorDate }, { merge: true });
    setProfil((p) => ({ ...p, injectionAnchorDate: suggestedAnchorDate }));
    setAnchorSuggestionDismissed(true);
  };

  // Platform-kapu: natívon a dózis-számoló eszközök nem léteznek, ezért a
  // nevük SEM jelenhet meg gyorsműveletként (App Store 1.4.2 / APL531603) —
  // a natívon elérhető eszközökre mutatunk helyettük. Kapu: config/platform.ts.
  // ⚠️ A4 (2026-10-14): a build-mód feltétele SZÓ SZERINT itt áll, hogy a natív
  // buildben a webes ág (a rejtett eszközök nevei) fordításkor kiessen — a
  // futásidejű IS_NATIVE önmagában a nevet a bundle-ben hagyta. Ellenőrzés:
  // scripts/check-native-build.sh.
  const quickActions = process.env.REACT_APP_BUILD_MODE === "native" || IS_NATIVE
    ? [
        { label: "Új bejegyzés", icon: Plus, color: "bg-emerald-100 text-emerald-700", to: "/naplo" },
        { label: "Statisztika", icon: BarChart2, color: "bg-blue-100 text-blue-700", to: "/statisztika" },
        { label: "Súly predikátor", icon: TrendingDown, color: "bg-rose-100 text-rose-700", to: "/eszkozok" },
        { label: "Fehérje kalkulátor", icon: Target, color: "bg-orange-100 text-orange-700", to: "/eszkozok" },
      ]
    : [
        { label: "Új bejegyzés", icon: Plus, color: "bg-emerald-100 text-emerald-700", to: "/naplo" },
        { label: "Dózis kalkulátor", icon: Calculator, color: "bg-yellow-100 text-yellow-700", to: "/eszkozok" },
        { label: "GLP Plotter", icon: Activity, color: "bg-blue-100 text-blue-700", to: "/eszkozok" },
        { label: "Kimaradt injekció", icon: AlertCircle, color: "bg-red-100 text-red-700", to: "/eszkozok" },
      ];

  return (
    <div className="max-w-lg mx-auto px-4 pt-6 pb-6 space-y-4">

      {/* ── Hero ── */}
      <div className="relative rounded-2xl overflow-hidden p-5 text-white" style={{ background: "linear-gradient(135deg, #1a5c46 0%, #2d8f72 100%)" }}>
        {weightData.length > 1 && (
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weightData}>
                <Line type="monotone" dataKey="weight" stroke="white" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="relative z-10">
          <p className="text-white/60 text-xs font-medium mb-1">Következő injekció</p>
          {nextInjDate ? (
            <>
              <p className="text-2xl font-bold mb-0.5">
                {nextInjDate.toLocaleDateString("hu-HU", { month: "long", day: "numeric", weekday: "long" })}
              </p>
              <p className="text-white/70 text-sm mb-4">
                {daysUntil !== null && daysUntil > 0
                  ? `${daysUntil} nap múlva`
                  : daysUntil === 0
                    ? "Ma esedékes!"
                    : `${cycleState?.overdueDays} napja lejárt`}
              </p>
            </>
          ) : (
            <p className="text-lg font-semibold mb-4 text-white/80">Adj hozzá bejegyzést a naplóban</p>
          )}
          <div className="flex flex-wrap gap-2">
            {lastDose && <span className="bg-white/15 text-white text-xs px-3 py-1 rounded-full font-medium">{prevDose && prevDose !== lastDose ? `${prevDose} → ${lastDose} mg` : `${lastDose} mg`}</span>}
            {currentPhase && (
              <span className="bg-white/15 text-white text-xs px-3 py-1 rounded-full font-medium">
                {isOverdue ? "⏸️ Ciklus lejárt" : `${currentPhase.icon} ${currentPhase.nameHu}`}
              </span>
            )}
            {therapyWeek && <span className="bg-white/15 text-white text-xs px-3 py-1 rounded-full font-medium">{therapyWeek}. hét</span>}
          </div>
        </div>
      </div>

      {/* ── Injekciónap-javaslat (#86cb5dbkn) ── */}
      {suggestedAnchorDate && !anchorSuggestionDismissed && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 flex items-start gap-2">
          <Zap size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-amber-900">Úgy tűnik, mostanában más napon adod be, mint amit beállítottunk — frissítsük?</p>
            <div className="flex gap-2 mt-2">
              <button onClick={handleAcceptAnchorSuggestion} className="text-xs font-medium bg-amber-600 text-white px-3 py-1 rounded-lg hover:bg-amber-700">Frissítés</button>
              <button onClick={() => setAnchorSuggestionDismissed(true)} className="text-xs font-medium text-amber-700 px-3 py-1 rounded-lg hover:bg-amber-100">Most ne</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Stat kártyák ── */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-1.5 mb-2"><Activity size={13} className="text-gray-400" /><p className="text-xs text-gray-400">Jelenlegi súly</p></div>
          <p className="text-2xl font-bold text-gray-900">{lastWeight ? `${lastWeight} kg` : "–"}</p>
          {lastEntryDelta && Number(lastEntryDelta) > 0 && <p className="text-xs text-emerald-600 mt-0.5 flex items-center gap-0.5"><TrendingDown size={11} /> {lastEntryDelta} kg</p>}
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-1.5 mb-2"><TrendingDown size={13} className="text-gray-400" /><p className="text-xs text-gray-400">{weightChangeLabel(totalLoss, "total")}</p></div>
          {/* Növekedésnél semleges szín (B2, 2. szabály) — a szöveg előjele mondja meg az irányt. */}
          <p className={`text-2xl font-bold ${formatWeightChange(totalLoss).isGain ? "text-gray-800" : "text-emerald-600"}`}>{formatWeightChange(totalLoss).text}</p>
          {formatWeightChangePct(totalLossPct) && <p className="text-xs text-gray-400 mt-0.5">{formatWeightChangePct(totalLossPct)}</p>}
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-1.5 mb-2"><Syringe size={13} className="text-gray-400" /><p className="text-xs text-gray-400">Utolsó dózis</p></div>
          <p className="text-2xl font-bold text-gray-900">{lastDose ? `${lastDose} mg` : "–"}</p>
          {lastInjDate && <p className="text-xs text-gray-400 mt-0.5">{lastInjDate.toLocaleDateString("hu-HU", { month: "short", day: "numeric" })}</p>}
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-1.5 mb-2"><BarChart2 size={13} className="text-gray-400" /><p className="text-xs text-gray-400">Heti átlag</p></div>
          {weeklyAvgLossKg !== null ? <><p className="text-2xl font-bold text-emerald-600">-{weeklyAvgLossKg.toFixed(1)} kg</p><p className="text-xs text-gray-400 mt-0.5">/ hét</p></> : <p className="text-2xl font-bold text-gray-300">–</p>}
        </div>
      </div>

      {/* ── Célsúly progress ── */}
      {goalPct !== null && celSuly && lastWeight && (
        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Target size={15} className="text-emerald-700" /><p className="text-sm font-semibold text-gray-700">Célsúly haladás</p></div>
            <span className="text-xs font-bold text-emerald-700">{goalPct}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2.5 mb-3">
            <div className="bg-emerald-500 h-2.5 rounded-full transition-all duration-700" style={{ width: `${goalPct}%` }} />
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>Jelenlegi: <span className="font-semibold text-gray-800">{lastWeight} kg</span></span>
            {remainingKg !== null && remainingKg > 0 && <span>Még: <span className="font-semibold text-gray-800">-{remainingKg.toFixed(1)} kg</span></span>}
            <span>Cél: <span className="font-semibold text-gray-800">{celSuly} kg</span></span>
          </div>
          {goalPct >= 100 && <p className="text-center text-sm font-semibold text-emerald-600 mt-2">🎉 Elérted a célsúlyodat!</p>}
        </div>
      )}

      {/* ── Fázis szekció ── */}
      {currentPhase && cycleDays !== null && (
        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm space-y-4">
          <div className="flex items-center gap-2">
            <Zap size={15} className="text-emerald-700" />
            <p className="text-sm font-semibold text-gray-700">Hatásciklus</p>
          </div>

          {/* Fázis progress */}
          <div className="relative">
            <div className="absolute top-3 left-0 right-0 h-0.5 bg-gray-100" />
            <div className="absolute top-3 left-0 h-0.5 bg-emerald-500 transition-all" style={{ width: `${Math.min(100, (cycleDays / 6) * 100)}%` }} />
            <div className="relative flex justify-between">
              {PHASE_STEPS.map((step, i) => {
                const passed = cycleDays >= step.day;
                const active = i === currentPhaseIdx;
                return (
                  <div key={step.label} className="flex flex-col items-center gap-1.5">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${active ? "bg-emerald-600 border-emerald-600 scale-125" : passed ? "bg-emerald-500 border-emerald-500" : "bg-white border-gray-200"}`}>
                      {passed && !active && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                    </div>
                    <span className={`text-xs ${active ? "font-semibold text-emerald-700" : "text-gray-400"}`}>{step.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Aktuális fázis info — lejárt ciklusnál MÁS ágon (123qmzqympy).
              A fázis-szövegek azt feltételezik, hogy a ciklus fut; egy
              elmaradt injekciónál a 7. napi „Ma van az injekció!" tényszerűen
              hamis állítás az időzítésről. A lejárt ág ezért semleges és
              tényközlő: azt mondja el, mi a helyzet és mit lehet AZ APPBAN
              tenni — adagolásról nem nyilatkozik (CLAUDE.md 2. szabály).
              Éhség-badge sincs: lejárt ciklusnál nincs mihez kötni. */}
          {isOverdue ? (
            <div className="bg-gray-50 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xl">⏸️</span>
                <p className="text-sm font-semibold text-gray-800">Ciklus lejárt</p>
              </div>
              <p className="text-xs text-gray-600">
                Az utolsó rögzített injekció óta {cycleState?.rawDays} nap telt el — a beállított ütem {intervalDays} nap.
                A hatásciklus-fázisok addig érvényesek, amíg a ciklus fut.
              </p>
              <p className="text-xs text-gray-500 bg-white rounded-lg px-2 py-1.5">
                Ha azóta beadtad, rögzítsd a Naplóban — ezzel a ciklus újraindul. Ha az ütemezésed változott, a Profilban állíthatod.
              </p>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xl">{currentPhase.icon}</span>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{currentPhase.nameHu}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-lg font-medium ${currentPhase.hunger === "alacsony" ? "bg-emerald-100 text-emerald-700" : currentPhase.hunger === "közepes" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>
                    Éhség: {currentPhase.hunger}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-600">{currentPhase.happening}</p>
              <p className="text-xs text-gray-500 bg-white rounded-lg px-2 py-1.5">💡 {currentPhase.attention}</p>
            </div>
          )}
        </div>
      )}

      {/* ── Statisztika szekció ── */}
      {/* A fülsáv a nyers entries.length-től függ, NEM a periódus-szűrt
          weightData-tól, és NEM is a jövőbeli dátumot kizáró
          computableEntries-től — korábban a fülsáv is a szűkebb kapu
          mögött volt, így egy üres periódusra váltva (pl. "2 hét"), VAGY
          ha minden bejegyzés jövőbeli dátumú (computableEntries üres),
          a fülsáv is eltűnt, és nem maradt magyarázat/visszaút "Összes"
          nézetre (2026-08-17 QA-lelet). A entries.length===0 esetet a
          lap alján lévő "Még nincs adat" blokk fedi le. */}
      {entries.length > 0 && (
        <div className="space-y-4">
          {/* Időszűrő */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
            {(Object.keys(RANGE_LABELS) as TimeRange[]).map((r) => (
              <button key={r} onClick={() => setRange(r)}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${range === r ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}>
                {RANGE_LABELS[r]}
              </button>
            ))}
          </div>

          {weightData.length < 2 && (
            <div className="bg-gray-50 rounded-2xl p-8 text-center text-gray-400 text-sm">
              {ENTRIES_EMPTY_MESSAGES[selectEntriesEmptyReason(entries.length, computableEntries.length, weightData.length)]}
            </div>
          )}

          {weightData.length >= 2 && (
          <>
          {/* Összegzés */}
          {filtered.length > 1 && (() => {
            // "Összes" nézetben a lifetime kezdő súlyt mutatjuk (ugyanazt, mint
            // az "Összes fogyás" kártya, hogy ne legyen két eltérő "kezdő" szám
            // ugyanazon a lapon) — szűkebb időszaknál viszont az adott időszak
            // eleje a helyes jelentés ("mennyit fogytam az utolsó 2 hétben").
            // A súly-szűrés a calcPeriodSummary-n belül történik (A7, 2026-10-14):
            // súly nélküli utolsó bejegyzésnél is az utolsó MÉRT súly a jelenlegi.
            const { baseWeight: fw, lastWeight: lw, loss, lossPct } =
              calcPeriodSummary(filtered, profil.kezdoSuly, range === "all");
            const pct = formatWeightChangePct(lossPct);
            return (
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white rounded-2xl p-3 border border-gray-100 text-center">
                  <p className="text-xs text-gray-400 mb-1">Kezdő</p>
                  <p className="text-base font-bold text-gray-800">{fw} kg</p>
                </div>
                <div className="bg-white rounded-2xl p-3 border border-gray-100 text-center">
                  <p className="text-xs text-gray-400 mb-1">Jelenlegi</p>
                  <p className="text-base font-bold text-gray-800">{lw} kg</p>
                </div>
                <div className="bg-emerald-50 rounded-2xl p-3 border border-emerald-100 text-center">
                  <p className="text-xs text-emerald-600 mb-1">{weightChangeLabel(loss, "period")}</p>
                  <p className={`text-base font-bold ${formatWeightChange(loss).isGain ? "text-gray-800" : "text-emerald-700"}`}>{formatWeightChange(loss).text}</p>
                  {pct && <p className="text-xs text-emerald-500">{pct}</p>}
                </div>
              </div>
            );
          })()}

          {/* Súlygrafikon */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gray-700">Súly változás</p>
              <button onClick={() => setShowDoses(!showDoses)}
                className={`text-xs px-3 py-1 rounded-lg border transition-all ${showDoses ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-500 border-gray-200"}`}>
                {showDoses ? "Dózisok elrejtése" : "Dózisok mutatása"}
              </button>
            </div>
            {showDoses && usedDoses.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {usedDoses.map(d => (
                  <div key={d} className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full border-2 border-white shadow" style={{ background: doseColors[d] || "#6B7280" }} />
                    <span className="text-xs text-gray-500">{d} mg</span>
                  </div>
                ))}
              </div>
            )}
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={weightData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis domain={["dataMin - 1", "dataMax + 1"]} tick={{ fontSize: 10 }} width={58} tickFormatter={formatKgTick} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v: any) => [formatKgTick(v), "Súly"]} />
                <Line type="monotone" dataKey="weight" stroke="#059669" strokeWidth={2.5} dot={<CustomDot />} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Klinikai összehasonlítás — docs/KLINIKAI_ADATOK.md */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
            {!clinicalBranch || !clinicalMatchedDose ? (
              <>
                <p className="text-sm font-semibold text-gray-700 mb-2">Fogyás vs. klinikai vizsgálat</p>
                {clinicalEmptyReason === "missing_therapy_reason" ? (
                  <p className="text-sm text-gray-400">
                    Az összehasonlításhoz add meg a Profilban, miért kaptad a terápiát.{" "}
                    <Link to="/profil" className="text-emerald-600 underline">Profil megnyitása</Link>
                  </p>
                ) : (
                  <p className="text-sm text-gray-400">
                    Az összehasonlításhoz add meg a készítményt és a terápia okát a Profilban.
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-gray-700 mb-3">Fogyás vs. {clinicalBranch.trialName} vizsgálat</p>
                <div className="flex flex-wrap gap-3 mb-3">
                  <div className="flex items-center gap-1"><div className="w-4 h-0.5 rounded bg-gray-400" /><span className="text-xs text-gray-500">{formatComparatorLabel(clinicalBranch.comparatorLabel, IS_NATIVE)}</span></div>
                  <div className="flex items-center gap-1"><div className="w-4 h-0.5 rounded bg-blue-600" /><span className="text-xs text-gray-500">{formatReferenceDoseLabel(clinicalMatchedDose.doseMg, IS_NATIVE)}</span></div>
                  <div className="flex items-center gap-1"><div className="w-4 h-0.5 rounded bg-black" /><span className="text-xs text-gray-500">Te</span></div>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" type="number" tick={{ fontSize: 10 }} tickFormatter={formatWeekTick} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} width={50} tickFormatter={formatPercentTick} />
                    <Tooltip formatter={(v: any) => formatPercentTick(Number(v))} labelFormatter={(label: any) => formatWeekTick(Number(label))} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Line data={clinicalComparatorSeries} type="monotone" dataKey="pct" stroke="#9CA3AF" strokeWidth={1.5} dot={false} name={formatComparatorLabel(clinicalBranch.comparatorLabel, IS_NATIVE)} />
                    <Line data={clinicalReferenceSeries} type="monotone" dataKey="pct" stroke="#2563EB" strokeWidth={1.5} dot={false} name={formatReferenceDoseLabel(clinicalMatchedDose.doseMg, IS_NATIVE)} />
                    <Line data={userTrialData} type="monotone" dataKey="yourPct" stroke="#111" strokeWidth={2.5} dot={false} name="Te" />
                  </LineChart>
                </ResponsiveContainer>
                <p className="text-xs text-gray-400 mt-2">
                  {clinicalBranch.trialName} vizsgálat · {clinicalBranch.weeks} hét · {clinicalBranch.populationLabel}.
                  {clinicalBranch.derived && formatDerivedNote(IS_NATIVE)}
                  {" "}A köztes értékek interpoláltak.{" "}
                  <button type="button" onClick={() => openExternal("/tudastar/")} className="text-emerald-600 underline">Részletek és forrás →</button>
                </p>
                <p className="text-xs text-gray-400 mt-1">Csak tájékoztató.</p>
              </>
            )}
          </div>
          </>
          )}
        </div>
      )}

      {/* ── Quick actions ── */}
      <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
        <p className="text-sm font-semibold text-gray-700 mb-3">Quick actions</p>
        <div className="space-y-1">
          {quickActions.map(({ label, icon: Icon, color, to }) => (
            <button key={label} onClick={() => navigate(to)}
              className="w-full flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}><Icon size={15} /></div>
              <span className="text-sm font-medium text-gray-700">{label}</span>
              <ChevronRight size={14} className="text-gray-300 ml-auto" />
            </button>
          ))}
        </div>
      </div>

      {entries.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm space-y-2">
          <p className="text-3xl">📋</p>
          <p>Még nincs adat — adj hozzá első bejegyzést!</p>
          <button onClick={() => navigate("/naplo")}
            className="bg-emerald-700 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-emerald-800 transition-colors">
            Napló megnyitása →
          </button>
        </div>
      )}
    </div>
  );
}
